/**
 * SVG Edge Renderer
 *
 * Renders StreamlainEdges as SVG paths overlaid on the canvas.
 * Implements IEdgeRenderer interface for swappable rendering strategies.
 */

import { cssVarV2 } from '@streamlain/theme/v2';

import type { IEdgeRenderer } from '../strategies/renderer/types';
import type {
  EdgeEndpointMarker,
  EdgeVisualType,
  Point,
  StreamlainEdge,
  ViewportState,
} from '../types';

/** Automation edges share the same semantic primary color end-to-end. */
const AUTOMATION_EDGE_COLOR = cssVarV2('button/primary');

/** Edge colors by visual type */
const EDGE_COLORS: Record<EdgeVisualType, string> = {
  'automation-input': AUTOMATION_EDGE_COLOR,
  'automation-output': AUTOMATION_EDGE_COLOR,
  relation: '#f59e0b', // Orange (amber-500)
};

/** Highlighted edge colors (slightly brighter) */
const EDGE_COLORS_HIGHLIGHTED: Record<EdgeVisualType, string> = {
  'automation-input': AUTOMATION_EDGE_COLOR,
  'automation-output': AUTOMATION_EDGE_COLOR,
  relation: '#fbbf24', // Orange (amber-400)
};

/** Default stroke width */
const STROKE_WIDTH = 2;

/** Automation edges carry the primary canvas workflow and need more weight. */
const AUTOMATION_STROKE_WIDTH = STROKE_WIDTH * 2;

/** Highlighted stroke width */
const STROKE_WIDTH_HIGHLIGHTED = 3;

const AUTOMATION_STROKE_WIDTH_HIGHLIGHTED = STROKE_WIDTH_HIGHLIGHTED * 2;

/** Transition duration for smooth highlighting */
const TRANSITION_DURATION = '150ms';

/** Selection outline color (blue) */
const SELECTION_COLOR = '#3b82f6';

/** Selection outline extends beyond the rendered edge on both sides. */
const SELECTION_OUTLINE_WIDTH = 4;

/** Hitbox stroke width (invisible, for easier clicking) */
const HITBOX_STROKE_WIDTH = 20;

/** Length occupied by the target arrow head in SVG user units. */
const ARROW_HEAD_LENGTH = 20;

const ENDPOINT_MARKER_SIZE = 20;
const TERMINAL_MASK_STROKE_WIDTH =
  AUTOMATION_STROKE_WIDTH_HIGHLIGHTED + SELECTION_OUTLINE_WIDTH + 2;

/** Marker types for cardinality visualization */
export type MarkerType = EdgeEndpointMarker;

/** Marker configuration for an edge */
export interface EdgeMarkers {
  source: MarkerType;
  target: MarkerType;
}

/** Marker registry for edges */
const edgeMarkers = new Map<string, EdgeMarkers>();

/**
 * Resolves the visual type for color lookup.
 * Falls back to 'automation-output' for legacy edges without visualType.
 */
function resolveVisualType(edge: StreamlainEdge): EdgeVisualType {
  if (edge.visualType) return edge.visualType;
  return edge.type === 'relation' ? 'relation' : 'automation-output';
}

function resolveStrokeWidth(edge: StreamlainEdge): number {
  return edge.highlighted
    ? AUTOMATION_STROKE_WIDTH_HIGHLIGHTED
    : AUTOMATION_STROKE_WIDTH;
}

function resolveSelectionStrokeWidth(edge: StreamlainEdge): number {
  return resolveStrokeWidth(edge) + SELECTION_OUTLINE_WIDTH;
}

export interface EdgeTargetOverlay {
  clipBound: { x: number; y: number; w: number; h: number };
  endpoint: 'source' | 'target';
  stackingElement?: Element | null;
  zIndex: number;
}

interface TargetOverlayLayer {
  group: SVGGElement;
  markerSuffix: string;
  svg: SVGSVGElement;
}

/**
 * SVG-based edge renderer
 *
 * Creates an SVG overlay that renders edges as path elements.
 * Supports opacity, highlighting, and click detection.
 */
export class SVGEdgeRenderer implements IEdgeRenderer {
  private svg: SVGSVGElement | null = null;
  private container: HTMLElement | null = null;
  private mountContainer: HTMLElement | null = null;
  private edgeGroup: SVGGElement | null = null;
  private readonly targetOverlayLayers = new Map<number, TargetOverlayLayer>();
  private readonly targetOverlayLayerByEdge = new Map<string, number>();
  private readonly pathElements: Map<string, SVGPathElement> = new Map();
  private readonly markerElements: Map<string, SVGPathElement> = new Map();
  private readonly targetOverlayPaths = new Map<string, SVGPathElement>();
  private readonly targetOverlayMarkers = new Map<string, SVGPathElement>();
  private readonly targetOverlayClips = new Map<
    string,
    { clip: SVGClipPathElement; id: string; rect: SVGRectElement }
  >();
  private readonly hitboxElements: Map<string, SVGPathElement> = new Map();
  private readonly selectionElements: Map<string, SVGPathElement> = new Map();
  private readonly terminalMasks: Map<
    string,
    { id: string; mask: SVGMaskElement; terminalPath: SVGPathElement }
  > = new Map();
  private readonly edgeData: Map<string, StreamlainEdge> = new Map();
  private resizeObserver: ResizeObserver | null = null;
  private removeWindowResizeListener: (() => void) | null = null;
  private lastSvgHeight = 0;
  private lastSvgWidth = 0;
  private terminalMaskSequence = 0;
  private _viewport: ViewportState | null = null;

  constructor(
    private readonly resolveEdgeZIndex: (edge: StreamlainEdge) => number = () =>
      0,
    private readonly resolveTargetOverlay: (
      edge: StreamlainEdge
    ) => EdgeTargetOverlay | null = () => null
  ) {}

  /** Gets current viewport (for debugging/external access) */
  get viewport(): ViewportState | null {
    return this._viewport;
  }

  mount(container: HTMLElement): void {
    this.container = container;
    this.mountContainer =
      container.querySelector<HTMLElement>('gfx-viewport') ?? container;

    // Create SVG element
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('id', 'streamlain-edge-svg');
    this.svg.setAttribute('preserveAspectRatio', 'none');
    // Keep workflow edges and endpoint handles visible where they meet cards.
    this.svg.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: block;
      pointer-events: none;
      z-index: 0;
      overflow: visible;
    `;

    // Add marker definitions
    const defs = this.createMarkerDefinitions();
    this.svg.appendChild(defs);

    // Add a group for edges that will be transformed with viewport
    const edgeGroup = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'g'
    );
    edgeGroup.setAttribute('id', 'streamlain-edges');
    this.svg.appendChild(edgeGroup);
    this.edgeGroup = edgeGroup;

    // Mount inside gfx-viewport so the edge z-index participates in the same
    // stacking context as GFX blocks and surface canvases.
    this.mountContainer.prepend(this.svg);
    this.syncSvgSizeFromContainer();
    this.startContainerResizeSync(container);
  }

  unmount(): void {
    this.stopContainerResizeSync();
    this.svg?.remove();
    for (const { svg } of this.targetOverlayLayers.values()) svg.remove();
    this.svg = null;
    this.container = null;
    this.mountContainer = null;
    this.edgeGroup = null;
    this.targetOverlayLayers.clear();
    this.targetOverlayLayerByEdge.clear();
    this.lastSvgHeight = 0;
    this.lastSvgWidth = 0;
    this.terminalMaskSequence = 0;
    this.pathElements.clear();
    this.markerElements.clear();
    this.targetOverlayPaths.clear();
    this.targetOverlayMarkers.clear();
    this.targetOverlayClips.clear();
    this.hitboxElements.clear();
    this.selectionElements.clear();
    this.terminalMasks.clear();
    this.edgeData.clear();
  }

  render(edges: StreamlainEdge[], viewport: ViewportState): void {
    if (!this.svg) return;

    this.updateViewport(viewport);
    const visibleEdges = edges.filter(edge => edge.visible && edge.path);
    const edgeZIndex = visibleEdges.length
      ? Math.min(...visibleEdges.map(edge => this.resolveEdgeZIndex(edge)))
      : 0;
    this.svg.style.zIndex = String(Math.max(0, edgeZIndex));
    // Get edge group
    const edgeGroup = this.edgeGroup;
    if (!edgeGroup) return;

    // Track which edges are still active
    const activeEdgeIds = new Set<string>();

    // Render each edge
    for (const edge of edges) {
      if (!edge.visible || !edge.path) continue;

      activeEdgeIds.add(edge.id);
      this.edgeData.set(edge.id, edge);

      // Create/update hitbox element (invisible, for easier clicking)
      let hitboxEl = this.hitboxElements.get(edge.id);
      if (!hitboxEl) {
        hitboxEl = this.createHitboxElement(edge);
        edgeGroup.appendChild(hitboxEl);
        this.hitboxElements.set(edge.id, hitboxEl);
      }
      if (edge.path) {
        hitboxEl.setAttribute('d', edge.path);
      }

      // Create/update visible path element
      let pathEl = this.pathElements.get(edge.id);
      if (!pathEl) {
        pathEl = this.createPathElement(edge);
        edgeGroup.appendChild(pathEl);
        this.pathElements.set(edge.id, pathEl);
      }

      let markerEl = this.markerElements.get(edge.id);
      if (!markerEl) {
        markerEl = this.createMarkerElement();
        edgeGroup.appendChild(markerEl);
        this.markerElements.set(edge.id, markerEl);
      }

      // Update path attributes
      this.updatePathElement(pathEl, markerEl, edge);
      this.updateTargetOverlay(edge);
    }

    // Remove edges that are no longer active
    for (const [edgeId, pathEl] of this.pathElements) {
      if (!activeEdgeIds.has(edgeId)) {
        pathEl.remove();
        this.pathElements.delete(edgeId);
        this.markerElements.get(edgeId)?.remove();
        this.markerElements.delete(edgeId);
        this.removeTargetOverlay(edgeId);
        this.selectionElements.get(edgeId)?.remove();
        this.selectionElements.delete(edgeId);
        this.removeTerminalMask(edgeId);
        // Also remove hitbox
        const hitboxEl = this.hitboxElements.get(edgeId);
        if (hitboxEl) {
          hitboxEl.remove();
          this.hitboxElements.delete(edgeId);
        }
        this.edgeData.delete(edgeId);
      }
    }
    this.removeUnusedTargetOverlayLayers();
  }

  updateEdge(edge: StreamlainEdge): void {
    const pathEl = this.pathElements.get(edge.id);
    const markerEl = this.markerElements.get(edge.id);
    if (pathEl && markerEl) {
      this.edgeData.set(edge.id, edge);
      this.updatePathElement(pathEl, markerEl, edge);
      this.updateTargetOverlay(edge);
      this.removeUnusedTargetOverlayLayers();
    }
  }

  updateViewport(viewport: ViewportState): void {
    if (!this.svg) return;

    this._viewport = viewport;
    this.updateSvgSize(viewport);
    this.updateViewBox(viewport);
  }

  removeEdge(edgeId: string): void {
    const pathEl = this.pathElements.get(edgeId);
    if (pathEl) {
      pathEl.remove();
      this.pathElements.delete(edgeId);
    }
    this.markerElements.get(edgeId)?.remove();
    this.markerElements.delete(edgeId);
    this.removeTargetOverlay(edgeId);
    this.selectionElements.get(edgeId)?.remove();
    this.selectionElements.delete(edgeId);
    this.removeTerminalMask(edgeId);
    const hitboxEl = this.hitboxElements.get(edgeId);
    if (hitboxEl) {
      hitboxEl.remove();
      this.hitboxElements.delete(edgeId);
    }
    this.edgeData.delete(edgeId);
    this.removeUnusedTargetOverlayLayers();
  }

  hitTest(point: Point): StreamlainEdge | null {
    if (!this.svg) return null;

    // Use SVG's native hit testing
    const elements = document.elementsFromPoint(point.x, point.y);

    for (const el of elements) {
      if (el instanceof SVGPathElement && el.dataset.edgeId) {
        const edge = this.edgeData.get(el.dataset.edgeId);
        if (edge) return edge;
      }
    }

    return null;
  }

  setOpacity(edgeId: string, opacity: number): void {
    const pathEl = this.pathElements.get(edgeId);
    if (pathEl) {
      pathEl.setAttribute('opacity', String(opacity));
    }
    this.markerElements.get(edgeId)?.setAttribute('opacity', String(opacity));
    this.targetOverlayPaths
      .get(edgeId)
      ?.setAttribute('opacity', String(opacity));
    this.targetOverlayMarkers
      .get(edgeId)
      ?.setAttribute('opacity', String(opacity));

    const edge = this.edgeData.get(edgeId);
    if (edge) {
      edge.opacity = opacity;
    }
  }

  setHighlight(edgeId: string, highlighted: boolean): void {
    const pathEl = this.pathElements.get(edgeId);
    const markerEl = this.markerElements.get(edgeId);
    const edge = this.edgeData.get(edgeId);

    if (pathEl && markerEl && edge) {
      edge.highlighted = highlighted;
      this.updatePathElement(pathEl, markerEl, edge);

      const selectionEl = this.selectionElements.get(edgeId);
      if (selectionEl) {
        selectionEl.setAttribute(
          'stroke-width',
          String(resolveSelectionStrokeWidth(edge))
        );
      }

      // Bring highlighted edges to front
      if (highlighted) {
        this.bringEdgeToFront(edgeId);
      }
    }
  }

  setSelected(edgeId: string, selected: boolean): void {
    const pathEl = this.pathElements.get(edgeId);
    const edge = this.edgeData.get(edgeId);

    if (!pathEl || !edge) return;

    edge.selected = selected;

    if (selected) {
      // Create selection outline (blue glow behind the edge)
      let selectionEl = this.selectionElements.get(edgeId);

      if (!selectionEl) {
        selectionEl = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        );
        selectionEl.setAttribute('fill', 'none');
        selectionEl.setAttribute('stroke', SELECTION_COLOR);
        selectionEl.setAttribute(
          'stroke-width',
          String(resolveSelectionStrokeWidth(edge))
        );
        selectionEl.setAttribute('stroke-linecap', 'round');
        selectionEl.setAttribute('stroke-linejoin', 'round');
        selectionEl.setAttribute('opacity', '0.5');
        selectionEl.style.pointerEvents = 'none';

        // Insert before the main path (behind it)
        pathEl.parentElement?.insertBefore(selectionEl, pathEl);
        this.selectionElements.set(edgeId, selectionEl);
      }

      // Update selection path to match edge path
      if (edge.path) {
        selectionEl.setAttribute('d', edge.path);
      }
      const terminalMask = this.terminalMasks.get(edgeId);
      if (terminalMask) {
        selectionEl.setAttribute('mask', `url(#${terminalMask.id})`);
      } else {
        selectionEl.removeAttribute('mask');
      }
      selectionEl.style.display = '';

      // Bring both selection and path to front
      this.bringEdgeToFront(edgeId);
    } else {
      // Hide/remove selection outline
      const selectionEl = this.selectionElements.get(edgeId);
      if (selectionEl) {
        selectionEl.style.display = 'none';
      }
    }
  }

  /**
   * Keeps SVG user units aligned with CSS pixels.
   *
   * Firefox can keep an inline SVG's default 300x150 user coordinate space
   * during reload timing if only CSS width/height are set. Edge paths are
   * calculated in screen pixels after the viewport transform, so the overlay
   * must expose a matching viewport explicitly.
   */
  private updateSvgSize(viewport: ViewportState): void {
    if (!this.svg) return;

    if (this.lastSvgWidth > 0 && this.lastSvgHeight > 0) return;

    this.applySvgSize(viewport.width, viewport.height);
  }

  private startContainerResizeSync(container: HTMLElement): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        const size = this.getResizeObserverSize(entries[0]);
        if (size) {
          this.applySvgSize(size.width, size.height);
          return;
        }
        this.syncSvgSizeFromContainer();
      });
      this.resizeObserver.observe(container);
      return;
    }

    const onResize = () => {
      this.syncSvgSizeFromContainer();
    };
    window.addEventListener('resize', onResize);
    this.removeWindowResizeListener = () => {
      window.removeEventListener('resize', onResize);
    };
  }

  private stopContainerResizeSync(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.removeWindowResizeListener?.();
    this.removeWindowResizeListener = null;
  }

  private syncSvgSizeFromContainer(): boolean {
    if (!this.svg || !this.container) return false;

    const rect = this.container?.getBoundingClientRect();
    const width = rect?.width ?? 0;
    const height = rect?.height ?? 0;

    if (width <= 0 || height <= 0) return false;

    this.applySvgSize(width, height);
    return true;
  }

  private getResizeObserverSize(
    entry: ResizeObserverEntry | undefined
  ): { width: number; height: number } | null {
    if (!entry) return null;

    const borderBoxSize = entry.borderBoxSize;
    const boxSize = Array.isArray(borderBoxSize)
      ? borderBoxSize[0]
      : borderBoxSize;
    if (boxSize) {
      if (boxSize.inlineSize <= 0 || boxSize.blockSize <= 0) return null;
      return {
        width: boxSize.inlineSize,
        height: boxSize.blockSize,
      };
    }

    const { width, height } = entry.contentRect;
    if (width <= 0 || height <= 0) return null;
    return { width, height };
  }

  private applySvgSize(width: number, height: number): void {
    if (!this.svg) return;
    if (width === this.lastSvgWidth && height === this.lastSvgHeight) return;

    const widthString = String(width);
    const heightString = String(height);
    this.svg.setAttribute('width', widthString);
    this.svg.setAttribute('height', heightString);
    this.svg.setAttribute('viewBox', `0 0 ${widthString} ${heightString}`);
    this.svg.style.width = `${widthString}px`;
    this.svg.style.height = `${heightString}px`;
    for (const { svg } of this.targetOverlayLayers.values()) {
      svg.setAttribute('width', widthString);
      svg.setAttribute('height', heightString);
      svg.setAttribute('viewBox', `0 0 ${widthString} ${heightString}`);
      svg.style.width = `${widthString}px`;
      svg.style.height = `${heightString}px`;
    }
    this.lastSvgWidth = width;
    this.lastSvgHeight = height;
  }

  /**
   * Updates the SVG viewBox to match the canvas viewport
   */
  private updateViewBox(viewport: ViewportState): void {
    if (!this.svg) return;

    const { viewportX, viewportY, zoom } = viewport;

    // Transform: Model coordinates → Screen coordinates
    // Formula: screenX = (modelX - viewportX) * zoom
    //          screenY = (modelY - viewportY) * zoom
    // As matrix: matrix(zoom, 0, 0, zoom, -viewportX*zoom, -viewportY*zoom)
    const edgeGroup = this.edgeGroup;
    if (edgeGroup) {
      const transform = `matrix(${zoom}, 0, 0, ${zoom}, ${-viewportX * zoom}, ${-viewportY * zoom})`;
      this.applyViewportTransform(edgeGroup, transform);
      for (const { group } of this.targetOverlayLayers.values()) {
        this.applyViewportTransform(group, transform);
      }
    }
  }

  private applyViewportTransform(group: SVGGElement, transform: string): void {
    group.style.transformBox = 'view-box';
    group.style.transformOrigin = '0 0';
    group.style.willChange = 'transform';
    if (group.style.transform !== transform) {
      group.style.transform = transform;
    }
  }

  /**
   * Creates an invisible hitbox element for easier clicking (20px wide)
   */
  private createHitboxElement(edge: StreamlainEdge): SVGPathElement {
    const hitbox = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );

    // Store edge ID for hit testing
    hitbox.dataset.edgeId = edge.id;

    // Make it invisible but clickable
    hitbox.setAttribute('fill', 'none');
    hitbox.setAttribute('stroke', 'transparent');
    hitbox.setAttribute('stroke-width', String(HITBOX_STROKE_WIDTH));
    hitbox.setAttribute('stroke-linecap', 'round');
    hitbox.setAttribute('stroke-linejoin', 'round');
    hitbox.style.pointerEvents = 'stroke';
    hitbox.style.cursor = 'pointer';

    return hitbox;
  }

  /**
   * Creates a new SVG path element for an edge (visible)
   */
  private createPathElement(_edge: StreamlainEdge): SVGPathElement {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    // No pointer events - hitbox handles clicking
    path.style.pointerEvents = 'none';

    // Add CSS transition for smooth highlighting
    path.style.transition = `opacity ${TRANSITION_DURATION} ease-in-out, stroke ${TRANSITION_DURATION} ease-in-out, stroke-width ${TRANSITION_DURATION} ease-in-out`;

    // Visual attributes
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');

    return path;
  }

  /** Carries endpoint markers independently from the masked visible stroke. */
  private createMarkerElement(): SVGPathElement {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-opacity', '0');
    path.style.pointerEvents = 'none';
    return path;
  }

  /**
   * Updates an existing path element with edge data
   */
  private updatePathElement(
    pathEl: SVGPathElement,
    markerEl: SVGPathElement,
    edge: StreamlainEdge
  ): void {
    // Path data
    if (edge.path) {
      pathEl.setAttribute('d', edge.path);
      markerEl.setAttribute('d', edge.path);
    }

    // Styling
    const vt = resolveVisualType(edge);
    const colors = edge.highlighted ? EDGE_COLORS_HIGHLIGHTED : EDGE_COLORS;
    pathEl.setAttribute('stroke', colors[vt]);
    pathEl.setAttribute('stroke-width', String(resolveStrokeWidth(edge)));
    pathEl.setAttribute('opacity', String(edge.opacity));
    markerEl.setAttribute('stroke', colors[vt]);
    markerEl.setAttribute('stroke-width', String(resolveStrokeWidth(edge)));
    markerEl.setAttribute('opacity', String(edge.opacity));

    const markers = edgeMarkers.get(edge.id) ?? {
      source: edge.sourceMarker ?? 'none',
      target: edge.targetMarker ?? 'none',
    };

    // Relation edges remain dashed. Arrow edges keep the complete stroke path;
    // a terminal SVG mask makes only the area underneath the arrow transparent.
    if (edge.type === 'relation') {
      pathEl.setAttribute('stroke-dasharray', '8 4');
      pathEl.removeAttribute('mask');
      this.removeTerminalMask(edge.id);
    } else if (markers.target === 'arrow') {
      pathEl.removeAttribute('stroke-dasharray');
      this.applyArrowHeadMask(pathEl, edge);
    } else {
      pathEl.removeAttribute('stroke-dasharray');
      pathEl.removeAttribute('mask');
      this.removeTerminalMask(edge.id);
    }

    // Markers live on an unmasked carrier. Their context-stroke follows normal
    // and connected-block-highlight colors without exposing the carrier line.
    const overlay = this.resolveTargetOverlay(edge);
    if (markers.source !== 'none' && overlay?.endpoint !== 'source') {
      markerEl.setAttribute(
        'marker-start',
        `url(#${markers.source}-${vt}-start)`
      );
    } else {
      markerEl.removeAttribute('marker-start');
    }
    if (markers.target !== 'none' && overlay?.endpoint !== 'target') {
      markerEl.setAttribute('marker-end', `url(#${markers.target}-${vt}-end)`);
    } else {
      markerEl.removeAttribute('marker-end');
    }

    // Visibility
    pathEl.style.display = edge.visible ? '' : 'none';
    markerEl.style.display = edge.visible ? '' : 'none';
  }

  /**
   * Foreground copy for the short path section hidden by an automation card.
   * The clip overlaps the outer card edge by half a stroke. Both copies are
   * opaque and identical, so this covers the card border/shadow without a
   * visible sub-pixel gap at the layer boundary.
   */
  private updateTargetOverlay(edge: StreamlainEdge): void {
    const overlay = this.resolveTargetOverlay(edge);
    if (!overlay || !edge.path) {
      this.removeTargetOverlay(edge.id);
      return;
    }
    const layer = this.ensureTargetOverlayLayer(
      overlay.zIndex,
      overlay.stackingElement
    );
    const previousLayerZIndex = this.targetOverlayLayerByEdge.get(edge.id);
    if (
      previousLayerZIndex !== undefined &&
      previousLayerZIndex !== overlay.zIndex
    ) {
      this.removeTargetOverlay(edge.id);
    }
    this.targetOverlayLayerByEdge.set(edge.id, overlay.zIndex);
    const { group, markerSuffix } = layer;

    let path = this.targetOverlayPaths.get(edge.id);
    if (!path) {
      path = this.createPathElement(edge);
      group.append(path);
      this.targetOverlayPaths.set(edge.id, path);
    }
    let marker = this.targetOverlayMarkers.get(edge.id);
    if (!marker) {
      marker = this.createMarkerElement();
      group.append(marker);
      this.targetOverlayMarkers.set(edge.id, marker);
    }

    const clip = this.ensureTargetOverlayClip(edge.id, layer.svg);
    const { x, y, w, h } = overlay.clipBound;
    const vt = resolveVisualType(edge);
    const colors = edge.highlighted ? EDGE_COLORS_HIGHLIGHTED : EDGE_COLORS;
    const seamOverlap = resolveStrokeWidth(edge) / 2 + 1;
    clip.rect.setAttribute('x', String(x - seamOverlap));
    clip.rect.setAttribute('y', String(y - seamOverlap));
    clip.rect.setAttribute('width', String(w + seamOverlap * 2));
    clip.rect.setAttribute('height', String(h + seamOverlap * 2));
    for (const element of [path, marker]) {
      element.setAttribute('d', edge.path ?? '');
      element.setAttribute('stroke', colors[vt]);
      element.setAttribute('stroke-width', String(resolveStrokeWidth(edge)));
      element.setAttribute('opacity', String(edge.opacity));
      element.setAttribute('clip-path', `url(#${clip.id})`);
    }
    const terminalMask = this.terminalMasks.get(edge.id);
    if (overlay.endpoint === 'target' && terminalMask) {
      path.setAttribute('mask', `url(#${terminalMask.id})`);
    } else {
      path.removeAttribute('mask');
    }
    path.removeAttribute('stroke-dasharray');

    const markers = edgeMarkers.get(edge.id) ?? {
      source: edge.sourceMarker ?? 'none',
      target: edge.targetMarker ?? 'none',
    };
    marker.removeAttribute('marker-start');
    marker.removeAttribute('marker-end');
    if (overlay.endpoint === 'source' && markers.source !== 'none') {
      marker.setAttribute(
        'marker-start',
        `url(#${markers.source}-${vt}-start${markerSuffix})`
      );
    }
    if (overlay.endpoint === 'target' && markers.target !== 'none') {
      marker.setAttribute(
        'marker-end',
        `url(#${markers.target}-${vt}-end${markerSuffix})`
      );
    }
  }

  private ensureTargetOverlayLayer(
    zIndex: number,
    stackingElement?: Element | null
  ): TargetOverlayLayer {
    const existing = this.targetOverlayLayers.get(zIndex);
    if (existing) {
      this.placeOverlayAfterConnectedBlock(existing.svg, stackingElement);
      return existing;
    }

    if (!this.svg || !this.mountContainer) {
      throw new Error('Target edge overlay mount point unavailable');
    }
    const markerSuffix = `-overlay-${zIndex}`;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('streamlain-edge-target-overlay');
    svg.dataset.parentZIndex = String(zIndex);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.cssText = this.svg.style.cssText;
    svg.style.zIndex = String(zIndex);
    svg.appendChild(this.createMarkerDefinitions(markerSuffix));
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.classList.add('streamlain-edge-target-overlays');
    if (this._viewport) {
      const { viewportX, viewportY, zoom } = this._viewport;
      this.applyViewportTransform(
        group,
        `matrix(${zoom}, 0, 0, ${zoom}, ${-viewportX * zoom}, ${-viewportY * zoom})`
      );
    }
    svg.appendChild(group);
    this.placeOverlayAfterConnectedBlock(svg, stackingElement);
    const layer = { group, markerSuffix, svg };
    this.targetOverlayLayers.set(zIndex, layer);
    this.applyOverlayLayerSize(svg);
    return layer;
  }

  private placeOverlayAfterConnectedBlock(
    svg: SVGSVGElement,
    stackingElement?: Element | null
  ): void {
    if (
      stackingElement?.parentElement === this.mountContainer &&
      stackingElement.nextElementSibling !== svg
    ) {
      stackingElement.insertAdjacentElement('afterend', svg);
      return;
    }
    if (!svg.isConnected) this.mountContainer?.append(svg);
  }

  private applyOverlayLayerSize(svg: SVGSVGElement): void {
    if (this.lastSvgWidth <= 0 || this.lastSvgHeight <= 0) return;
    const width = String(this.lastSvgWidth);
    const height = String(this.lastSvgHeight);
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;
  }

  private removeUnusedTargetOverlayLayers(): void {
    const used = new Set(this.targetOverlayLayerByEdge.values());
    for (const [zIndex, layer] of this.targetOverlayLayers) {
      if (used.has(zIndex)) continue;
      layer.svg.remove();
      this.targetOverlayLayers.delete(zIndex);
    }
  }

  private ensureTargetOverlayClip(
    edgeId: string,
    svg: SVGSVGElement
  ): {
    clip: SVGClipPathElement;
    id: string;
    rect: SVGRectElement;
  } {
    const existing = this.targetOverlayClips.get(edgeId);
    if (existing) return existing;
    const defs = svg.querySelector('defs');
    if (!defs) throw new Error('Target edge overlay definitions unavailable');
    const id = `streamlain-edge-target-clip-${this.terminalMaskSequence++}`;
    const clip = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'clipPath'
    );
    clip.setAttribute('id', id);
    clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    clip.append(rect);
    defs.append(clip);
    const result = { clip, id, rect };
    this.targetOverlayClips.set(edgeId, result);
    return result;
  }

  private removeTargetOverlay(edgeId: string): void {
    this.targetOverlayPaths.get(edgeId)?.remove();
    this.targetOverlayPaths.delete(edgeId);
    this.targetOverlayMarkers.get(edgeId)?.remove();
    this.targetOverlayMarkers.delete(edgeId);
    this.targetOverlayClips.get(edgeId)?.clip.remove();
    this.targetOverlayClips.delete(edgeId);
    this.targetOverlayLayerByEdge.delete(edgeId);
  }

  private applyArrowHeadMask(
    pathEl: SVGPathElement,
    edge: StreamlainEdge
  ): void {
    if (
      !edge.path ||
      typeof pathEl.getTotalLength !== 'function' ||
      typeof pathEl.getPointAtLength !== 'function'
    ) {
      pathEl.removeAttribute('mask');
      this.removeTerminalMask(edge.id);
      return;
    }

    const totalLength = pathEl.getTotalLength();
    const arrowStart = pathEl.getPointAtLength(
      Math.max(0, totalLength - ARROW_HEAD_LENGTH)
    );
    const endpoint = pathEl.getPointAtLength(totalLength);
    const terminalDx = endpoint.x - arrowStart.x;
    const terminalDy = endpoint.y - arrowStart.y;
    const terminalLength = Math.hypot(terminalDx, terminalDy);
    const endpointMaskExtension = TERMINAL_MASK_STROKE_WIDTH;
    const maskedEndpoint =
      terminalLength > 0
        ? {
            x:
              endpoint.x +
              (terminalDx / terminalLength) * endpointMaskExtension,
            y:
              endpoint.y +
              (terminalDy / terminalLength) * endpointMaskExtension,
          }
        : endpoint;
    const terminalMask = this.ensureTerminalMask(edge.id);
    const fullPath = terminalMask.mask.firstElementChild as SVGPathElement;
    fullPath.setAttribute('d', edge.path);
    terminalMask.terminalPath.setAttribute(
      'd',
      `M ${arrowStart.x} ${arrowStart.y} L ${maskedEndpoint.x} ${maskedEndpoint.y}`
    );
    pathEl.setAttribute('mask', `url(#${terminalMask.id})`);

    const selectionEl = this.selectionElements.get(edge.id);
    selectionEl?.setAttribute('mask', `url(#${terminalMask.id})`);
  }

  private ensureTerminalMask(edgeId: string): {
    id: string;
    mask: SVGMaskElement;
    terminalPath: SVGPathElement;
  } {
    const existing = this.terminalMasks.get(edgeId);
    if (existing) return existing;

    const defs = this.svg?.querySelector('defs');
    if (!defs) throw new Error('SVG edge renderer definitions are unavailable');

    const id = `streamlain-edge-terminal-mask-${this.terminalMaskSequence++}`;
    const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask');
    mask.setAttribute('id', id);
    mask.setAttribute('maskUnits', 'userSpaceOnUse');
    mask.setAttribute('maskContentUnits', 'userSpaceOnUse');
    mask.setAttribute('x', '-1000000');
    mask.setAttribute('y', '-1000000');
    mask.setAttribute('width', '2000000');
    mask.setAttribute('height', '2000000');

    const fullPath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );
    fullPath.setAttribute('fill', 'none');
    fullPath.setAttribute('stroke', 'white');
    fullPath.setAttribute('stroke-width', String(TERMINAL_MASK_STROKE_WIDTH));
    fullPath.setAttribute('stroke-linecap', 'round');
    fullPath.setAttribute('stroke-linejoin', 'round');

    const terminalPath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );
    terminalPath.setAttribute('fill', 'none');
    terminalPath.setAttribute('stroke', 'black');
    terminalPath.setAttribute(
      'stroke-width',
      String(TERMINAL_MASK_STROKE_WIDTH)
    );
    terminalPath.setAttribute('stroke-linecap', 'butt');

    mask.append(fullPath, terminalPath);
    defs.append(mask);
    const result = { id, mask, terminalPath };
    this.terminalMasks.set(edgeId, result);
    return result;
  }

  private removeTerminalMask(edgeId: string): void {
    const terminalMask = this.terminalMasks.get(edgeId);
    terminalMask?.mask.remove();
    this.terminalMasks.delete(edgeId);
    this.selectionElements.get(edgeId)?.removeAttribute('mask');
  }

  private bringEdgeToFront(edgeId: string): void {
    const pathEl = this.pathElements.get(edgeId);
    const markerEl = this.markerElements.get(edgeId);
    const selectionEl = this.selectionElements.get(edgeId);
    if (!pathEl?.parentElement) return;

    if (selectionEl && selectionEl.style.display !== 'none') {
      pathEl.parentElement.appendChild(selectionEl);
    }
    pathEl.parentElement.appendChild(pathEl);
    if (markerEl) pathEl.parentElement.appendChild(markerEl);
  }

  /**
   * Creates SVG marker definitions for arrows and crowsfoot
   */
  private createMarkerDefinitions(idSuffix = ''): SVGDefsElement {
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

    // Create markers for all visual types and both ends
    const visualTypes: EdgeVisualType[] = [
      'automation-input',
      'automation-output',
      'relation',
    ];

    for (const name of visualTypes) {
      // Arrow marker - end (points INTO the target node, opposite to path direction)
      const arrowEnd = this.createArrowMarker(
        `arrow-${name}-end${idSuffix}`,
        'end'
      );
      defs.appendChild(arrowEnd);

      // Arrow marker - start (points INTO the source node, opposite to path direction)
      const arrowStart = this.createArrowMarker(
        `arrow-${name}-start${idSuffix}`,
        'start'
      );
      defs.appendChild(arrowStart);

      const dotEnd = this.createDotMarker(`dot-${name}-end${idSuffix}`);
      defs.appendChild(dotEnd);

      const dotStart = this.createDotMarker(`dot-${name}-start${idSuffix}`);
      defs.appendChild(dotStart);

      // Crowsfoot marker - end (points INTO the target node)
      const crowsfootEnd = this.createCrowsfootMarker(
        `crowsfoot-${name}-end${idSuffix}`,
        'end'
      );
      defs.appendChild(crowsfootEnd);

      // Crowsfoot marker - start (points INTO the source node)
      const crowsfootStart = this.createCrowsfootMarker(
        `crowsfoot-${name}-start${idSuffix}`,
        'start'
      );
      defs.appendChild(crowsfootStart);
    }

    return defs;
  }

  /**
   * Creates an arrow marker
   *
   * Markers always point INTO the node (towards the connection point):
   * - 'start' marker: at start of path, points backwards (into source node)
   * - 'end' marker: at end of path, points forwards (into target node)
   *
   * With orient="auto-start-reverse" for start markers, SVG automatically
   * flips the marker to point in the correct direction.
   */
  private createArrowMarker(
    id: string,
    position: 'start' | 'end'
  ): SVGMarkerElement {
    const marker = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'marker'
    );
    marker.setAttribute('id', id);
    marker.setAttribute('markerWidth', String(ENDPOINT_MARKER_SIZE));
    marker.setAttribute('markerHeight', String(ENDPOINT_MARKER_SIZE));
    marker.setAttribute('viewBox', '0 0 20 20');
    marker.setAttribute('refX', '20');
    marker.setAttribute('refY', '10');
    // 'auto-start-reverse' flips start markers to point INTO the source node
    marker.setAttribute(
      'orient',
      position === 'start' ? 'auto-start-reverse' : 'auto'
    );
    marker.setAttribute('markerUnits', 'userSpaceOnUse');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M0,0 L20,10 L0,20 Z');
    path.setAttribute('fill', 'context-stroke');

    marker.appendChild(path);
    return marker;
  }

  /** Creates a compact circular source handle centered on the endpoint. */
  private createDotMarker(id: string): SVGMarkerElement {
    const marker = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'marker'
    );
    marker.setAttribute('id', id);
    marker.setAttribute('markerWidth', String(ENDPOINT_MARKER_SIZE));
    marker.setAttribute('markerHeight', String(ENDPOINT_MARKER_SIZE));
    marker.setAttribute('viewBox', '0 0 20 20');
    marker.setAttribute('refX', '10');
    marker.setAttribute('refY', '10');
    marker.setAttribute('orient', 'auto');
    marker.setAttribute('markerUnits', 'userSpaceOnUse');

    const circle = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'circle'
    );
    circle.setAttribute('cx', '10');
    circle.setAttribute('cy', '10');
    circle.setAttribute('r', '7');
    circle.setAttribute('fill', 'context-stroke');
    marker.appendChild(circle);
    return marker;
  }

  /**
   * Creates a crowsfoot marker (for "many" cardinality)
   * Looks like: ─<  (three lines spreading into the node)
   *
   * Uses the same orient strategy as arrows: auto-start-reverse for start markers.
   */
  private createCrowsfootMarker(
    id: string,
    position: 'start' | 'end'
  ): SVGMarkerElement {
    const marker = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'marker'
    );
    marker.setAttribute('id', id);
    marker.setAttribute('markerWidth', '12');
    marker.setAttribute('markerHeight', '12');
    marker.setAttribute('refX', '12'); // Reference from the spreading end
    marker.setAttribute('refY', '6');
    // 'auto-start-reverse' flips start markers to point INTO the source node
    marker.setAttribute(
      'orient',
      position === 'start' ? 'auto-start-reverse' : 'auto'
    );
    marker.setAttribute('markerUnits', 'strokeWidth');

    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('stroke', 'context-stroke');
    g.setAttribute('stroke-width', '1.5');
    g.setAttribute('fill', 'none');

    // Crowsfoot: three lines spreading from vertex at right to fan at left
    // Lines from (12,6) spreading to (0,0), (0,6), (0,12)
    const line1 = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'line'
    );
    line1.setAttribute('x1', '12');
    line1.setAttribute('y1', '6');
    line1.setAttribute('x2', '0');
    line1.setAttribute('y2', '0');

    const line2 = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'line'
    );
    line2.setAttribute('x1', '12');
    line2.setAttribute('y1', '6');
    line2.setAttribute('x2', '0');
    line2.setAttribute('y2', '6');

    const line3 = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'line'
    );
    line3.setAttribute('x1', '12');
    line3.setAttribute('y1', '6');
    line3.setAttribute('x2', '0');
    line3.setAttribute('y2', '12');

    g.appendChild(line1);
    g.appendChild(line2);
    g.appendChild(line3);

    marker.appendChild(g);
    return marker;
  }

  /**
   * Sets markers for an edge (for cardinality visualization)
   */
  setMarkers(edgeId: string, markers: EdgeMarkers): void {
    edgeMarkers.set(edgeId, markers);

    // Update the path element if it exists
    const pathEl = this.pathElements.get(edgeId);
    const markerEl = this.markerElements.get(edgeId);
    const edge = this.edgeData.get(edgeId);
    if (pathEl && markerEl && edge) {
      this.updatePathElement(pathEl, markerEl, edge);
    }
  }

  /**
   * Removes markers from an edge
   */
  clearMarkers(edgeId: string): void {
    edgeMarkers.delete(edgeId);

    const pathEl = this.pathElements.get(edgeId);
    if (pathEl) {
      pathEl.removeAttribute('marker-start');
      pathEl.removeAttribute('marker-end');
    }
    const markerEl = this.markerElements.get(edgeId);
    markerEl?.removeAttribute('marker-start');
    markerEl?.removeAttribute('marker-end');
  }
}
